import { useState } from "react";
import { usePymbleEventSource } from "./usePymbleEventSource";
import { api_s2 } from "../config";

/**
 * Streaming AI helper for the pastoral drawer's inline note assist.
 *
 * Trimmed from my-pymble-portal's useStreamingAgent to the surface the drawer
 * uses: streamingData (accumulated content chunks), connectionClosed, and
 * retrieveStreamingData. POSTs to `${api_s2.baseURL}ai/` with { agent_id, data }.
 */
export function useStreamingAgent() {
	const { callEventSource } = usePymbleEventSource();
	const [streamingData, setStreamingData] = useState<string[]>([]);
	const [connectionClosed, setConnectionClosed] = useState(true);
	const [responseId, setResponseId] = useState<string | null>(null);
	const aiEndpoint = "ai/";

	const isValidJSON = (str: string): boolean => {
		if (typeof str !== "string" || str.trim() === "") return false;
		try {
			JSON.parse(str);
			return true;
		} catch {
			return false;
		}
	};

	const retrieveStreamingData = async (data: object, agent_id: string) => {
		setStreamingData([]);
		setConnectionClosed(false);
		setResponseId(null);

		const prompt = {
			body: JSON.stringify({ agent_id, data }),
			headers: { "Content-Type": "application/json" },
			onmessage(ev: { data: string; id?: string }) {
				const rawData = ev.data;
				if (isValidJSON(rawData) && isNaN(Number(rawData))) {
					const parsed = JSON.parse(rawData);
					if (parsed.content !== undefined) {
						setStreamingData((prev) => [...prev, parsed.content]);
					}
					if (parsed.id) setResponseId(parsed.id);
				} else {
					setStreamingData((prev) => [...prev, rawData]);
				}
			},
			onclose() {
				setTimeout(() => setConnectionClosed(true), 100);
			},
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			onerror(err: any) {
				console.error("Streaming error:", err);
				setConnectionClosed(true);
				setStreamingData([]);
				setResponseId(null);
			},
		};

		try {
			await callEventSource("POST", `${api_s2.baseURL}${aiEndpoint}`, prompt);
		} catch (error) {
			console.error("Error in retrieveStreamingData:", error);
			setConnectionClosed(true);
			setStreamingData([]);
			setResponseId(null);
		}
	};

	return { streamingData, connectionClosed, responseId, retrieveStreamingData };
}
